import {
  Banner,
  Box,
  Button,
  ButtonGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelSection,
  Text,
  PanelInset,
} from '@customerio/pluma-components/react';
import type { CopilotApprovalComponentProps } from '@customerio/hydra-copilot';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { analyzeBashCommand, getMutatingResources, type AnalysisResult } from './parse-curl-resources';
import { useApprovedResources } from './approved-resources-context';
import { useAnalytics } from '@/services/analytics';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { getToolCallLabels } from '../../utils/tool-call-labels';

// ---------------------------------------------------------------------------
// Internal helpers (never exposed to user)
// ---------------------------------------------------------------------------

function collectMutatingPaths(analysis: AnalysisResult | null): Set<string> {
  if (analysis == null) return new Set();
  return new Set(getMutatingResources(analysis.resources).map((r) => r.path));
}

function collectMutatingTypes(analysis: AnalysisResult | null): string[] {
  if (analysis == null) return [];
  const types = new Set(getMutatingResources(analysis.resources).map((r) => r.resourceType));
  return [...types];
}

function isCreateOnly(analysis: AnalysisResult | null): boolean {
  if (analysis == null) return false;
  const mutating = getMutatingResources(analysis.resources);
  return mutating.length > 0 && mutating.every((r) => r.action === 'create');
}

/** Humanize a resource type slug: "campaign-nodes" → "campaign nodes" */
function humanizeType(type: string): string {
  return type.replaceAll('-', ' ');
}

function buildAlwaysAllowLabel(types: string[], createOnly: boolean): string {
  const humanized = types.map(humanizeType);

  if (humanized.length === 0) return createOnly ? 'Always allow creating this' : 'Always allow for this';

  if (createOnly) {
    if (humanized.length === 1) return `Always allow creating ${humanized[0]}`;
    const last = humanized[humanized.length - 1];
    const rest = humanized.slice(0, -1).join(', ');
    return `Always allow creating ${rest} and ${last}`;
  }

  const singular = (s: string) => (s.endsWith('s') ? s.slice(0, -1) : s);
  if (humanized.length === 1) return `Always allow for this ${singular(humanized[0])}`;
  const last = humanized[humanized.length - 1];
  const rest = humanized.slice(0, -1).join(', ');
  return `Always allow for these ${rest} and ${last}`;
}

type Status = 'pending' | 'approved' | 'denied' | 'error';

function resolveStatus(isPending: boolean, isError: boolean, isRejected: boolean): Status {
  if (isPending) return 'pending';
  if (isError) return 'error';
  if (isRejected) return 'denied';
  return 'approved';
}

const STATUS_ICON: Record<
  Status,
  { name: 'check-circle' | 'stop-circle' | 'error'; color: 'success' | 'subtle' | 'critical' }
> = {
  pending: { name: 'check-circle', color: 'subtle' },
  approved: { name: 'check-circle', color: 'success' },
  denied: { name: 'stop-circle', color: 'subtle' },
  error: { name: 'error', color: 'critical' },
};

// ---------------------------------------------------------------------------
// Auto-approve hook
// ---------------------------------------------------------------------------

/**
 * Determine whether a tool call should be auto-approved.
 * Pure derived state — no effects needed for the decision itself.
 */
function shouldAutoApprove({
  isPending,
  isBash,
  hasDelete,
  coverage,
  isReadOnly,
  isAllApproved,
  mutPaths,
  areResourcesApproved,
}: {
  isPending: boolean;
  isBash: boolean;
  hasDelete: boolean;
  coverage: AnalysisResult['coverage'] | null;
  isReadOnly: boolean;
  isAllApproved: boolean;
  mutPaths: Set<string>;
  areResourcesApproved: (paths: Set<string>) => boolean;
}): boolean {
  if (!isPending) return false;

  // Bash-specific: auto-approve read-only commands when we've verified
  // every code path. `all_resources_found` (or better) guarantees we found
  // every curl invocation with a known method — even if some URLs have
  // unresolved variables, we know none of them are mutating.
  // Deletes always require explicit approval regardless of analysis coverage.
  if (isBash && coverage != null && coverage !== 'incomplete') {
    if (hasDelete) return false;

    if (isReadOnly) return true;
  }

  // Global toggle — works for all tools including unanalyzed bash
  if (isAllApproved) return true;

  // Per-resource approval (bash only, requires fully resolved URLs for exact
  // path matching). Works for both updates (exact resource path like
  // /campaigns/123) and creates (collection path like /campaigns). Deletes
  // are excluded above so they never reach this check.
  if (
    isBash &&
    coverage === 'all_resources_found_and_resolved' &&
    !hasDelete &&
    mutPaths.size > 0 &&
    areResourcesApproved(mutPaths)
  ) {
    return true;
  }

  return false;
}

/**
 * Auto-approve a tool call before the browser paints to avoid a flash
 * of the pending approval UI. Uses useLayoutEffect so onApprove fires
 * synchronously after render but before paint.
 */
function useAutoApprove(autoApprove: boolean, onApprove: () => void, onAutoApproved?: () => void) {
  const didAutoApprove = useRef(false);

  useLayoutEffect(() => {
    if (!autoApprove || didAutoApprove.current) return;
    didAutoApprove.current = true;
    onAutoApproved?.();
    onApprove();
  }, [autoApprove, onApprove, onAutoApproved]);

  return didAutoApprove.current || autoApprove;
}

// Caution banner above approve/deny when the workspace lets agents edit
// live data. Hidden when the flag is off. The "disable editing live
// campaigns" link is admin-only.
function AgentLiveDataBanner() {
  const allowEdit = useFeatureFlag(AccountFeatureFlag.AllowAgentEditLiveData);
  const { hasPermission } = usePermissions();
  const canEditAccount = hasPermission('account/edit') === true;

  if (!allowEdit) {
    return null;
  }

  return (
    <Banner variant="caution" title="This ask may edit things that are live.">
      <Text size="xs">
        {canEditAccount ? (
          <>
            You can{' '}
            <Link href="/settings/privacy" isUnderlined>
              disable editing live campaigns
            </Link>{' '}
            in AI settings.
          </>
        ) : (
          <>Live campaigns, sent newsletters, broadcasts, and in-use segments may all be modified.</>
        )}
      </Text>
    </Banner>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ApprovalComponentProps extends CopilotApprovalComponentProps {
  description?: React.ReactNode;
}

export function ApprovalComponent({ toolCall, onApprove, onDeny, description }: ApprovalComponentProps) {
  const isPending = toolCall.status === 'executing';
  const isError = toolCall.status === 'error';
  const { track } = useAnalytics();
  const decision = toolCall.response?.result as { status: 'approved' | 'rejected' } | undefined;
  const isRejected = !isPending && !isError && decision?.status === 'rejected';
  const status = resolveStatus(isPending, isError, isRejected);

  const { planned } = getToolCallLabels(toolCall);
  const { isAllApproved, setIsAllApproved, areResourcesApproved, approveResources } = useApprovedResources();

  const analysis = useMemo<AnalysisResult | null>(() => {
    if (toolCall.name !== 'bash') return null;
    const command = toolCall.args?.command;
    if (typeof command !== 'string') return null;
    return analyzeBashCommand(command);
  }, [toolCall.name, toolCall.args?.command]);

  const hasDelete = analysis != null && analysis.resources.some((r) => r.action === 'delete');
  const coverage = analysis?.coverage ?? null;
  const mutPaths = useMemo(() => collectMutatingPaths(analysis), [analysis]);
  const mutTypes = useMemo(() => collectMutatingTypes(analysis), [analysis]);
  const isReadOnly = coverage != null && coverage !== 'incomplete' && mutPaths.size === 0;
  const title = planned;

  // Show "always allow" when we can identify the resource type being mutated.
  // For updates this targets a specific resource (PUT /campaigns/123); for creates
  // it targets the collection (POST /campaigns). Both are useful for building up
  // per-type approval so the user isn't prompted for every single operation.
  // Deletes are always excluded — they must be approved individually.
  const hasTargetedResources = coverage === 'all_resources_found_and_resolved' && !hasDelete && mutPaths.size > 0;
  const onlyCreates = isCreateOnly(analysis);
  const showResourceOption = hasTargetedResources;

  const autoApprove = shouldAutoApprove({
    isPending,
    isBash: toolCall.name === 'bash',
    hasDelete,
    coverage,
    isReadOnly,
    isAllApproved,
    mutPaths,
    areResourcesApproved,
  });
  const onAutoApproved = useCallback(() => {
    track('aiAssistant', 'autoApprovalTriggered', { 'Tool Name': toolCall.name });
  }, [track, toolCall.name]);
  const isAutoApproving = useAutoApprove(autoApprove, onApprove, onAutoApproved);

  // Auto-approving — render nothing to avoid a flash of the pending UI
  if (isAutoApproving) {
    return null;
  }

  // Resolved states — compact inline
  if (status !== 'pending') {
    const icon = STATUS_ICON[status];
    return (
      <Panel>
        <PanelInset>
          <InlineStack alignBlock="center" gap="075" p="150">
            <Icon name={icon.name} color={icon.color} size="sm" />
            <Text size="xs" color={status === 'error' ? 'critical' : 'subtle'}>
              {status === 'error' ? (
                <>
                  <strong>Error:</strong> {title}
                </>
              ) : (
                title
              )}
            </Text>
          </InlineStack>
        </PanelInset>
      </Panel>
    );
  }

  // Pending state
  return (
    <Panel>
      <PanelSection>
        <Box display="flex" flexDirection="column" gap="200">
          <Box display="flex" flexDirection="column" gap="050">
            <Text size="s" weight="medium">
              {title}
            </Text>
            {description != null ? (
              <Text size="xs" color="subtle">
                {description}
              </Text>
            ) : null}
          </Box>
          {/* Skip when auto-approve is on — the chat-input banner already covers it. */}
          {!isAllApproved ? <AgentLiveDataBanner /> : null}

          <InlineStack alignBlock="center" gap="075" wrap>
            <Button
              size="sm"
              variant="subtle"
              onClick={() => {
                track('aiAssistant', 'approvalDenied', { 'Tool Name': toolCall.name });
                onDeny();
              }}
            >
              Deny
            </Button>

            {!hasDelete ? (
              <ButtonGroup variant="primary" size="small">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    track('aiAssistant', 'approvalAllowed', { 'Approval Type': 'once', 'Tool Name': toolCall.name });
                    onApprove();
                  }}
                >
                  Allow
                </Button>
                <DropdownMenu>
                  <DropdownMenuTriggerButton
                    size="sm"
                    variant="primary"
                    icon="chevron-down"
                    isIconOnly={true}
                    aria-label="More approval options"
                  />
                  <DropdownMenuContent style={{ maxWidth: 320 }}>
                    {showResourceOption ? (
                      <DropdownMenuItem
                        description={`Automatically approve ${onlyCreates ? 'creating' : 'changes to'} ${mutTypes
                          .map(humanizeType)
                          .join(', ')} for the rest of this session.`}
                        onClick={() => {
                          track('aiAssistant', 'approvalAllowed', {
                            'Approval Type': 'resource',
                            'Tool Name': toolCall.name,
                          });
                          approveResources(mutPaths);
                          onApprove();
                        }}
                      >
                        {buildAlwaysAllowLabel(mutTypes, onlyCreates)}
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      isDanger={true}
                      description="Automatically approve all future changes for the rest of this session."
                      onClick={() => {
                        track('aiAssistant', 'approvalAllowed', {
                          'Approval Type': 'all',
                          'Tool Name': toolCall.name,
                        });
                        setIsAllApproved(true);
                        onApprove();
                      }}
                    >
                      Allow all changes
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </ButtonGroup>
            ) : (
              <Button
                size="sm"
                variant="primary"
                isDanger={true}
                onClick={() => {
                  track('aiAssistant', 'approvalAllowed', { 'Approval Type': 'once', 'Tool Name': toolCall.name });
                  onApprove();
                }}
              >
                Delete
              </Button>
            )}
          </InlineStack>
        </Box>
      </PanelSection>
    </Panel>
  );
}

// Public API re-exports
export { ApprovedResourcesProvider, useApprovedResources } from './approved-resources-context';
